import { PERSISTENCE, type BlockPresetId } from "@/config/designer";
import type { Fixing } from "@/core/fixings/types";
import type { GlossDab } from "@/core/finish/types";

/**
 * Autosave, entirely on the user's machine.
 *
 * The sculpting field is a plain Float32Array, so it goes into IndexedDB as an
 * ArrayBuffer with no serialisation cost. Nothing is uploaded anywhere: there
 * is no account, no server and no network call in this application at all.
 */

export interface SavedDesign {
  version: number;
  presetId: BlockPresetId;
  width: number;
  height: number;
  depth: number;
  voxelSize: number;
  field: ArrayBuffer;
  dirty: ArrayBuffer;
  fixings: Fixing[];
  /** Optional so V1 saves made before finish painting continue to open. */
  glossDabs?: GlossDab[];
  savedAt: number;
}

export interface SavedDesignSummary {
  presetId: BlockPresetId;
  width: number;
  height: number;
  depth: number;
  savedAt: number;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const request = indexedDB.open(PERSISTENCE.databaseName, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(PERSISTENCE.storeName)) {
        db.createObjectStore(PERSISTENCE.storeName);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB open failed"));
    request.onblocked = () => reject(new Error("IndexedDB blocked"));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDatabase();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(PERSISTENCE.storeName, mode);
      const request = run(tx.objectStore(PERSISTENCE.storeName));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
    });
  } finally {
    db.close();
  }
}

export async function saveDesign(design: SavedDesign): Promise<boolean> {
  try {
    await withStore("readwrite", (store) => store.put(design, PERSISTENCE.recordKey));
    return true;
  } catch (error) {
    console.warn("[ethos] autosave failed", error);
    return false;
  }
}

function isValid(record: unknown): record is SavedDesign {
  if (!record || typeof record !== "object") return false;
  const d = record as Partial<SavedDesign>;
  return (
    d.version === PERSISTENCE.schemaVersion &&
    typeof d.presetId === "string" &&
    typeof d.width === "number" &&
    typeof d.height === "number" &&
    typeof d.depth === "number" &&
    typeof d.voxelSize === "number" &&
    d.field instanceof ArrayBuffer &&
    d.dirty instanceof ArrayBuffer &&
    Array.isArray(d.fixings)
  );
}

export async function loadDesign(): Promise<SavedDesign | null> {
  try {
    const record = await withStore<unknown>("readonly", (store) =>
      store.get(PERSISTENCE.recordKey),
    );
    if (!isValid(record)) {
      if (record !== undefined) {
        // A save from an older schema, or a corrupt one. Drop it rather than
        // leaving the app unable to start.
        await clearDesign();
      }
      return null;
    }
    return record;
  } catch (error) {
    console.warn("[ethos] could not read the saved design", error);
    return null;
  }
}

export async function loadSummary(): Promise<SavedDesignSummary | null> {
  const design = await loadDesign();
  if (!design) return null;
  return {
    presetId: design.presetId,
    width: design.width,
    height: design.height,
    depth: design.depth,
    savedAt: design.savedAt,
  };
}

export async function clearDesign(): Promise<void> {
  try {
    await withStore("readwrite", (store) => store.delete(PERSISTENCE.recordKey));
  } catch (error) {
    console.warn("[ethos] could not clear the saved design", error);
  }
}
